import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Target, Share2, Monitor, Search, Video, Bot, Headphones, Sparkles, MessageSquare, ArrowRight,
} from 'lucide-react';

const iconMap = { Target, Share2, Monitor, Search, Video, Bot, Headphones, Sparkles, MessageSquare };

const servicesShowcase = [
  { slug: 'paid-media-advertising', title: 'Paid Media Advertising',   icon: 'Target',
    tagline: 'Every click, every channel, every dollar — optimized.',
    hook: 'Full-funnel paid campaigns across Google, Meta, LinkedIn, TikTok, Programmatic, and CTV — orchestrated as one system, not seven.',
    stat: '320%', statLabel: 'average ROI',
    accent: ['#00f0ff', '#8a2be2'],
  },
  { slug: 'search-engine-optimization', title: 'SEO', icon: 'Search',
    tagline: 'Own page 1 for the searches that pay you.',
    hook: 'Technical SEO, content clusters, backlink strategy, and semantic optimization for topical authority Google actually rewards.',
    stat: '47', statLabel: 'target keywords ranked page 1',
    accent: ['#ffb400', '#ff0080'],
  },
  { slug: 'website-design-development', title: 'Websites Built to Convert', icon: 'Monitor',
    tagline: 'Fast. Beautiful. Actually sells.',
    hook: 'Next.js / React builds with Core Web Vitals in the green, PageSpeed near 100, structured data, and CRO baked in from day one.',
    stat: '99/100', statLabel: 'PageSpeed + 42% conversion lift',
    accent: ['#8a2be2', '#00f0ff'],
  },
  { slug: 'social-media-management', title: 'Social Media Management', icon: 'Share2',
    tagline: 'Content that stops the scroll and moves the metric.',
    hook: 'Strategy, content production, community management, and paid amplification across every platform your audience actually uses.',
    stat: '+216%', statLabel: 'engagement rate for retail clients',
    accent: ['#ff0080', '#a855f7'],
  },
  { slug: 'video-production', title: 'Video Production', icon: 'Video',
    tagline: 'Story-first video that gets watched.',
    hook: 'Full-service production — from scripting and shooting to editing and platform-native cuts for YouTube, TikTok, Shorts, and Reels.',
    stat: '3M+', statLabel: 'organic views generated in 90 days',
    accent: ['#f43f5e', '#ffb400'],
  },
  { slug: 'ai-automation', title: 'AI Automation', icon: 'Bot',
    tagline: 'Let AI handle the busywork, not the buyer.',
    hook: 'LLM-powered lead scoring, personalized outbound, chatbot triage, and reporting automations — production-grade, not gimmicks.',
    stat: '84%', statLabel: 'of inbound leads auto-qualified',
    accent: ['#a855f7', '#00f0ff'],
  },
  { slug: 'llmo-optimization', title: 'LLMO — LLM Optimization', icon: 'Sparkles',
    tagline: 'Get cited by ChatGPT, Perplexity, and Gemini.',
    hook: 'Structured content, authority signals, and answer-engineered pages so AI answer machines quote your brand as the source.',
    stat: '+37', statLabel: 'AI citations gained per week',
    accent: ['#10d97f', '#8a2be2'],
  },
  { slug: 'aeo-optimization', title: 'AEO — Answer Engine Optimization', icon: 'MessageSquare',
    tagline: 'Own position zero on every question that matters.',
    hook: 'Featured-snippet targeting, FAQ schema, People-Also-Ask capture, and voice-search optimization to win the answer, not just the click.',
    stat: '#0', statLabel: 'position captured across 60+ queries',
    accent: ['#00f0ff', '#ff0080'],
  },
  { slug: 'business-operations-support', title: 'Business Operations Support', icon: 'Headphones',
    tagline: 'A dedicated team behind your marketing.',
    hook: 'Inbound triage, lead follow-up, appointment booking, and marketing operations support — so your team focuses on closing, not chasing.',
    stat: '<3min', statLabel: 'average lead response time',
    accent: ['#ffb400', '#00f0ff'],
  },
];

const ServiceCard = ({ service, index, total, progress, onClick }) => {
  const segment = 1 / total;
  const start = index * segment;
  const end   = start + segment;
  const fade  = segment * 0.015; // ultra-tight 1.5% crossfade — feels like a hard swap
  const isFirst = index === 0;
  const isLast  = index === total - 1;

  // Hard opacity swap: only one card is truly visible at a time.
  const opacity = useTransform(
    progress,
    [
      isFirst ? -0.001 : start - fade,
      isFirst ? -0.001 : start + fade,
      isLast  ? 1.001  : end   - fade,
      isLast  ? 1.001  : end   + fade,
    ],
    [0, 1, 1, 0]
  );

  // Subtle slide: card slides up-in and up-out (no 3D rotation to avoid flicker)
  const y = useTransform(
    progress,
    [
      isFirst ? -0.001 : start - fade,
      isFirst ? -0.001 : start + fade,
      isLast  ? 1.001  : end   - fade,
      isLast  ? 1.001  : end   + fade,
    ],
    [isFirst ? 0 : 40, 0, 0, isLast ? 0 : -40]
  );

  // Only the fully-opaque card captures clicks
  const pointerEvents = useTransform(opacity, (o) => (o > 0.5 ? 'auto' : 'none'));

  const Icon = iconMap[service.icon] || Target;
  const [c1, c2] = service.accent;

  return (
    <motion.div
      className="absolute inset-0 flex items-center justify-center will-change-transform"
      style={{ y, opacity, pointerEvents }}
      data-testid={`service-card-${service.slug}`}
    >
      <motion.div
        className="relative w-full max-w-6xl mx-auto px-4 sm:px-6 md:px-10"
      >
        {/* Glow */}
        <div
          className="absolute -inset-8 rounded-[48px] blur-[80px] opacity-60 pointer-events-none"
          style={{ background: `radial-gradient(circle at 30% 50%, ${c1}55 0%, ${c2}22 40%, transparent 70%)` }}
        />

        {/* Card — whole card is clickable to navigate to service page */}
        <button
          type="button"
          onClick={onClick}
          className="relative w-full grid md:grid-cols-[1.3fr_1fr] gap-5 md:gap-10 items-center rounded-3xl border backdrop-blur-2xl p-5 sm:p-6 md:p-12 text-left cursor-pointer transition-transform duration-200 hover:scale-[1.01] focus:outline-none focus:ring-2 focus:ring-white/30"
          style={{
            borderColor: 'rgba(255,255,255,0.10)',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))',
            boxShadow: `0 20px 80px rgba(0,0,0,0.4), inset 0 0 40px ${c1}10`,
          }}
          data-testid={`service-card-link-${service.slug}`}
          aria-label={`Explore ${service.title}`}
        >
          {/* LEFT: text */}
          <div>
            <div className="flex items-center gap-3 mb-3 md:mb-5">
              <div
                className="w-10 h-10 md:w-12 md:h-12 rounded-2xl flex items-center justify-center border shrink-0"
                style={{
                  background: `linear-gradient(135deg, ${c1}30, ${c2}10)`,
                  borderColor: `${c1}50`,
                  boxShadow: `0 0 30px ${c1}40`,
                }}
              >
                <Icon size={20} color={c1} strokeWidth={1.75} />
              </div>
              <span
                className="text-[10px] md:text-xs font-mono uppercase tracking-[0.3em] font-semibold"
                style={{ color: c1 }}
              >
                {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')} · Service
              </span>
            </div>
            <h3 className="font-heading text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.05] mb-3 md:mb-4">
              {service.title}
            </h3>
            <p className="text-white/85 text-sm sm:text-base md:text-xl leading-snug mb-3 md:mb-4 font-body max-w-xl">
              {service.tagline}
            </p>
            <p className="text-white/55 text-xs sm:text-sm md:text-[15px] leading-relaxed mb-4 md:mb-6 font-body max-w-xl line-clamp-4 md:line-clamp-none">
              {service.hook}
            </p>

            {/* Inline compact stat — visible only on mobile (replaces the big square) */}
            <div
              className="md:hidden flex items-center gap-3 mb-4 rounded-2xl border px-4 py-3"
              style={{
                borderColor: `${c1}40`,
                background: `linear-gradient(135deg, ${c1}18, ${c2}08)`,
              }}
            >
              <div
                className="font-heading font-black text-2xl tabular-nums leading-none whitespace-nowrap"
                style={{
                  background: `linear-gradient(135deg, ${c1}, ${c2})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {service.stat}
              </div>
              <div className="text-white/60 text-[11px] leading-tight">
                {service.statLabel}
              </div>
            </div>

            <span
              className="group inline-flex items-center gap-2 px-4 md:px-5 py-2.5 md:py-3 rounded-full bg-white/[0.06] hover:bg-white/[0.14] border border-white/15 text-white text-xs md:text-sm font-semibold backdrop-blur-md transition-colors"
              data-testid={`service-card-cta-${service.slug}`}
            >
              Explore {service.title.split(' ')[0]}
              <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
            </span>
          </div>

          {/* RIGHT: metric spotlight — desktop/tablet only */}
          <div className="relative hidden md:block">
            <div
              className="relative aspect-square max-w-[320px] mx-auto rounded-3xl border overflow-hidden flex flex-col items-center justify-center p-6"
              style={{
                borderColor: `${c1}40`,
                background: `radial-gradient(circle at 50% 40%, ${c1}25 0%, ${c2}10 60%, rgba(3,0,20,0.5) 100%)`,
                boxShadow: `0 0 60px ${c1}30, inset 0 0 40px ${c2}15`,
              }}
            >
              {/* rotating ring */}
              <div
                className="absolute inset-4 rounded-3xl border border-dashed"
                style={{
                  borderColor: `${c1}30`,
                  animation: 'spin-slow-svc 24s linear infinite',
                }}
              />
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40 mb-3">Recent proof</div>
              <div
                className="font-heading font-black text-4xl md:text-5xl tabular-nums leading-none mb-3 text-center whitespace-nowrap"
                style={{
                  background: `linear-gradient(135deg, ${c1}, ${c2})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: `0 0 40px ${c1}80`,
                }}
              >
                {service.stat}
              </div>
              <div className="text-white/60 text-xs md:text-sm text-center max-w-[220px]">
                {service.statLabel}
              </div>
            </div>
            <style>{`@keyframes spin-slow-svc { to { transform: rotate(360deg); } }`}</style>
          </div>
        </button>
      </motion.div>
    </motion.div>
  );
};

const ServicesScrollShowcase = () => {
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  // Progress rail
  const railWidth = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  return (
    <section
      id="services-showcase"
      ref={containerRef}
      className="relative bg-void"
      style={{ height: `${servicesShowcase.length * 100}vh` }}
      data-testid="services-scroll-section"
    >
      <div className="sticky top-0 h-screen w-full flex flex-col overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[10%] left-[10%] w-[500px] h-[500px] rounded-full bg-brand/[0.06] blur-[140px]" />
          <div className="absolute bottom-[10%] right-[10%] w-[500px] h-[500px] rounded-full bg-accent-pink/[0.06] blur-[140px]" />
        </div>
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
            backgroundSize: '80px 80px',
          }}
        />

        {/* Eyebrow header */}
        <div className="relative z-20 pt-6 md:pt-20 px-4 md:px-6 text-center shrink-0">
          <span className="inline-flex items-center gap-2 px-3 py-1 md:px-4 md:py-1.5 rounded-full border border-brand/30 bg-brand/10 text-brand text-[9px] md:text-xs font-mono tracking-[0.3em] uppercase font-semibold mb-2 md:mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Our Services
          </span>
          <h2 className="font-heading text-lg sm:text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-[1.15] max-w-4xl mx-auto">
            Nine services.{' '}
            <span className="bg-gradient-to-r from-brand via-accent-pink to-accent-cyan bg-clip-text text-transparent">
              One growth partner.
            </span>
          </h2>
        </div>

        {/* Filmstrip stage */}
        <div className="relative z-10 flex-1 flex items-center justify-center min-h-0 py-2">
          <div className="relative w-full h-full">
            {servicesShowcase.map((s, i) => (
              <ServiceCard
                key={s.slug}
                service={s}
                index={i}
                total={servicesShowcase.length}
                progress={scrollYProgress}
                onClick={() => navigate(`/services/${s.slug}`)}
              />
            ))}
          </div>
        </div>

        {/* Bottom progress rail */}
        <div className="relative z-30 px-4 md:px-10 pb-4 md:pb-10 shrink-0">
          <div className="max-w-5xl mx-auto">
            <div className="relative h-[3px] rounded-full bg-white/10 overflow-hidden mb-2">
              <motion.div
                className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-brand via-accent-pink to-accent-cyan"
                style={{ width: railWidth }}
              />
            </div>
            <div className="flex items-center justify-between text-[9px] md:text-[10px] font-mono uppercase tracking-[0.25em] text-white/40">
              <span>Scroll to explore</span>
              <span data-testid="services-progress-count">{servicesShowcase.length} services</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesScrollShowcase;
